import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  fetchVacancyReport,
  fetchVacancyReportSyncStatus,
  triggerVacancyReportSync,
  VacancyReportColumn,
  VacancyReportCharts,
  VacancyReportSyncRun,
} from '../services/vacancyReportService';
import { exportQueryResponse } from '../services/exportService';
import VacancyReportChartGrid from './VacancyReportCharts';

export type VacancyReportTab = 'planned-vs-actual' | 'vacancies' | 'overstaffing';

export interface VacancyReportContext {
  report_family: 'vacancy-report';
  active_tab: VacancyReportTab;
  report_title: string;
  report_view: string;
}

interface VacancyReportDashboardProps {
  activeTab: VacancyReportTab;
  onAskMore?: (contextQuery: string, contextData?: any) => void;
  onContextChange?: (context: VacancyReportContext) => void;
}

type SortDirection = 'asc' | 'desc';

interface TableSort {
  key: string;
  direction: SortDirection;
}

export const vacancyReportTabs: Array<[VacancyReportTab, string]> = [
  ['planned-vs-actual', 'Planned vs. Actual'],
  ['vacancies', 'Vacancies'],
  ['overstaffing', 'Overstaffing'],
];

const kpiLabels: Partial<Record<VacancyReportTab, { count: string; ageing: string }>> = {
  vacancies: { count: 'Open Vacancies', ageing: 'Ave Ageing Days' },
  overstaffing: { count: 'Open Overstaffing Events', ageing: 'Ave Ageing Days' },
};

const chartCountTitles: Partial<Record<VacancyReportTab, { byStore: string; byProvince: string }>> = {
  vacancies: { byStore: 'Open Vacancies by Store', byProvince: 'Open Vacancies by Province' },
  overstaffing: { byStore: 'Overstaffing by Store', byProvince: 'Overstaffing by Province' },
};

// Matches the table-visual titles used in the source Power BI report.
const tableTitles: Record<VacancyReportTab, string> = {
  'planned-vs-actual': 'Planned vs. Actual by Store and Contract Type',
  vacancies: 'Vacancies by Store and Contract Type',
  overstaffing: 'Overstaffing by Store and Contract Type',
};

const TableLoadingRows: React.FC<{ columnCount: number; rowCount?: number }> = ({ columnCount, rowCount = 8 }) => {
  const safeColumnCount = Math.max(columnCount, 1);
  return (
    <>
      {Array.from({ length: rowCount }).map((_, rowIndex) => (
        <tr key={`loading-row-${rowIndex}`} className="animate-pulse bg-white dark:bg-slate-900">
          {Array.from({ length: safeColumnCount }).map((__, columnIndex) => (
            <td key={`loading-cell-${rowIndex}-${columnIndex}`} className="px-4 py-3">
              <div className="h-4 w-full min-w-24 rounded bg-slate-200 dark:bg-slate-700" />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
};

const getReportTitle = (activeTab: VacancyReportTab): string =>
  vacancyReportTabs.find(([key]) => key === activeTab)?.[1] || 'Vacancy Report';

const valueText = (value: unknown): string => {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  return String(value);
};

const formatNumber = (value: unknown): string => {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return valueText(value);
  }
  return new Intl.NumberFormat('en-US').format(numeric);
};

const formatRecordCount = (value: number): string => new Intl.NumberFormat('en-US').format(value);

const isVarianceColumn = (columnKey: string): boolean => columnKey.endsWith('_variance');

const renderCellValue = (column: VacancyReportColumn, value: unknown): string => {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  if (column.numeric) {
    return formatNumber(value);
  }
  return String(value);
};

const compareTableValues = (first: unknown, second: unknown): number => {
  const firstNumber = Number(valueText(first).replace(/,/g, ''));
  const secondNumber = Number(valueText(second).replace(/,/g, ''));
  if (Number.isFinite(firstNumber) && Number.isFinite(secondNumber)) {
    return firstNumber - secondNumber;
  }

  const firstTime = Date.parse(valueText(first));
  const secondTime = Date.parse(valueText(second));
  if (Number.isFinite(firstTime) && Number.isFinite(secondTime)) {
    return firstTime - secondTime;
  }

  return valueText(first).localeCompare(valueText(second), undefined, {
    numeric: true,
    sensitivity: 'base',
  });
};

const sortRowsByColumn = (
  sourceRows: Record<string, unknown>[],
  sort: TableSort | null,
): Record<string, unknown>[] => {
  if (!sort) {
    return sourceRows;
  }

  const directionMultiplier = sort.direction === 'asc' ? 1 : -1;
  return [...sourceRows].sort((first, second) =>
    compareTableValues(first[sort.key], second[sort.key]) * directionMultiplier
  );
};

// Sync poll cadence. A reconcile runs ~20-25s server-side; poll every 2s with a
// generous ceiling (~3 min) so a slow Sage/SharePoint fetch still resolves
// rather than reporting a false failure.
const SYNC_POLL_INTERVAL_MS = 2000;
const SYNC_POLL_MAX_ATTEMPTS = 90;

const delay = (ms: number): Promise<void> => new Promise((resolve) => window.setTimeout(resolve, ms));

const VacancyReportDashboard: React.FC<VacancyReportDashboardProps> = ({ activeTab, onAskMore, onContextChange }) => {
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [columns, setColumns] = useState<VacancyReportColumn[]>([]);
  const [summary, setSummary] = useState<Record<string, any> | undefined>();
  const [charts, setCharts] = useState<VacancyReportCharts | undefined>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});
  const [hiddenColumnKeys, setHiddenColumnKeys] = useState<string[]>([]);
  const [showColumnMenu, setShowColumnMenu] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [isExporting, setIsExporting] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [tableSort, setTableSort] = useState<TableSort | null>(null);
  // Ave Ageing Days is always computed over Open events (matching BI, which
  // has no scope toggle - the KPI card's whole context is Open).
  const ageingScope = 'open';
  const columnMenuRef = useRef<HTMLDivElement | null>(null);
  // Guards against setState after unmount if the user navigates away mid-poll.
  const isMountedRef = useRef(true);

  const title = getReportTitle(activeTab);
  const kpi = kpiLabels[activeTab];
  const selectableColumns = useMemo(() => columns.filter((column) => !column.hidden), [columns]);
  const visibleColumns = useMemo(
    () => selectableColumns.filter((column) => !hiddenColumnKeys.includes(column.key)),
    [hiddenColumnKeys, selectableColumns]
  );
  const allColumnsVisible = selectableColumns.length > 0 && hiddenColumnKeys.length === 0;

  // Extracted so the "Sync now" button can re-fetch the current tab after a
  // manual reconciliation without duplicating the load/error handling. The
  // optional `signal` lets the mount effect cancel a stale in-flight load on
  // tab switch; a button-triggered refresh calls it with no signal.
  const loadReport = useCallback(
    async (signal?: { cancelled: boolean }) => {
      setIsLoading(true);
      setError(null);
      try {
        const report = await fetchVacancyReport(activeTab, 5000, ageingScope);
        if (!signal?.cancelled) {
          setRows(report.rows);
          setColumns(report.columns);
          setSummary(report.summary);
          setCharts(report.charts);
        }
      } catch (loadError) {
        if (!signal?.cancelled) {
          setRows([]);
          setColumns([]);
          setSummary(undefined);
          setCharts(undefined);
          setError(loadError instanceof Error ? loadError.message : 'Failed to load Vacancy Report data');
        }
      } finally {
        if (!signal?.cancelled) {
          setIsLoading(false);
        }
      }
    },
    [activeTab, ageingScope]
  );

  useEffect(() => {
    const signal = { cancelled: false };
    loadReport(signal);
    return () => {
      signal.cancelled = true;
    };
  }, [loadReport]);

  useEffect(() => {
    setColumnFilters({});
    setHiddenColumnKeys([]);
    setShowColumnMenu(false);
    setTableSort(null);
    setCurrentPage(1);
  }, [activeTab]);

  useEffect(() => {
    setCurrentPage(1);
  }, [columnFilters, hiddenColumnKeys, pageSize, tableSort]);

  useEffect(() => {
    if (!showColumnMenu) {
      return;
    }
    const handleClickOutside = (event: MouseEvent) => {
      if (!columnMenuRef.current?.contains(event.target as Node)) {
        setShowColumnMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showColumnMenu]);

  useEffect(() => {
    const selectableKeys = new Set(selectableColumns.map((column) => column.key));
    setHiddenColumnKeys((current) => current.filter((key) => selectableKeys.has(key)));
  }, [selectableColumns]);

  useEffect(() => {
    onContextChange?.({
      report_family: 'vacancy-report',
      active_tab: activeTab,
      report_title: title,
      report_view: 'table',
    });
  }, [activeTab, onContextChange, title]);

  useEffect(() => {
    if (!syncMessage) {
      return;
    }
    const timer = window.setTimeout(() => setSyncMessage(null), 6000);
    return () => window.clearTimeout(timer);
  }, [syncMessage]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      return visibleColumns.every((column) => {
        const filterValue = (columnFilters[column.key] || '').trim().toLowerCase();
        if (!filterValue) {
          return true;
        }
        return valueText(row[column.key]).toLowerCase().includes(filterValue);
      });
    });
  }, [columnFilters, rows, visibleColumns]);

  const sortedRows = useMemo(() => sortRowsByColumn(filteredRows, tableSort), [filteredRows, tableSort]);

  // Grand-total row (matches the "Total" row in the Power BI table visuals).
  // Planned vs. Actual sums its headcount columns; Overstaffing sums the two
  // count columns (Initial/Latest) - both mirror BI. Vacancies has no total
  // row in BI, so it gets none. "Ageing (Days)" is deliberately never summed
  // (BI leaves it blank in the total row - a sum of per-record ages is
  // meaningless). Summed over filteredRows so it reflects active column
  // filters, exactly like BI's total responds to slicers.
  const showTotalsRow = activeTab === 'planned-vs-actual' || activeTab === 'overstaffing';
  const isSummableColumn = (column: VacancyReportColumn): boolean =>
    Boolean(column.numeric) && column.key !== 'ageing_days';
  const columnTotals = useMemo<Record<string, number>>(() => {
    if (!showTotalsRow) {
      return {};
    }
    const totals: Record<string, number> = {};
    visibleColumns.forEach((column) => {
      if (!isSummableColumn(column)) {
        return;
      }
      totals[column.key] = filteredRows.reduce((sum, row) => {
        const value = Number(row[column.key]);
        return sum + (Number.isFinite(value) ? value : 0);
      }, 0);
    });
    return totals;
  }, [showTotalsRow, visibleColumns, filteredRows]);

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / pageSize));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const startIndex = (safeCurrentPage - 1) * pageSize;
  const displayedRows = sortedRows.slice(startIndex, startIndex + pageSize);
  const endIndex = Math.min(startIndex + displayedRows.length, sortedRows.length);
  const pageNumbers = useMemo(() => {
    const pages: number[] = [];
    const firstPage = Math.max(1, Math.min(safeCurrentPage - 2, totalPages - 4));
    const lastPage = Math.min(totalPages, firstPage + 4);
    for (let page = firstPage; page <= lastPage; page += 1) {
      pages.push(page);
    }
    return pages;
  }, [safeCurrentPage, totalPages]);

  const handleColumnFilterChange = (key: string, value: string) => {
    setColumnFilters((current) => ({ ...current, [key]: value }));
  };

  const handleTableSort = (columnKey: string) => {
    setTableSort((currentSort) => {
      if (currentSort?.key !== columnKey) {
        return { key: columnKey, direction: 'asc' };
      }
      return { key: columnKey, direction: currentSort.direction === 'asc' ? 'desc' : 'asc' };
    });
    setCurrentPage(1);
  };

  const handleToggleColumn = (columnKey: string) => {
    setHiddenColumnKeys((current) => {
      if (current.includes(columnKey)) {
        return current.filter((key) => key !== columnKey);
      }
      if (visibleColumns.length <= 1) {
        return current;
      }
      return [...current, columnKey];
    });
  };

  const handleSelectAllColumns = () => setHiddenColumnKeys([]);
  const handleHideAllColumns = () => {
    const [, ...columnsToHide] = selectableColumns;
    setHiddenColumnKeys(columnsToHide.map((column) => column.key));
  };

  // Polls the background sync until it leaves 'running', or the ceiling is hit.
  const pollSyncUntilDone = async (runId: number): Promise<VacancyReportSyncRun> => {
    for (let attempt = 0; attempt < SYNC_POLL_MAX_ATTEMPTS; attempt += 1) {
      await delay(SYNC_POLL_INTERVAL_MS);
      const run = await fetchVacancyReportSyncStatus(runId);
      if (run.status !== 'running') {
        return run;
      }
    }
    throw new Error('The sync is taking longer than expected. Please check back in a moment.');
  };

  const handleSync = async () => {
    if (isSyncing) return;
    setIsSyncing(true);
    setSyncMessage(null);
    try {
      // POST returns immediately (202) with a run_id; the reconcile runs in the
      // background on the server, so we poll its status instead of blocking.
      const started = await triggerVacancyReportSync();
      const finalRun = await pollSyncUntilDone(started.run_id);
      if (!isMountedRef.current) {
        return;
      }
      if (finalRun.status === 'succeeded' || finalRun.status === 'partial') {
        // Sync writes to our own vacancy_* tables; re-fetch the active tab so the
        // table, KPIs and charts reflect the freshly reconciled data.
        await loadReport();
        if (isMountedRef.current) {
          setSyncMessage({ type: 'success', text: 'Report refreshed with the latest data.' });
        }
      } else {
        setSyncMessage({
          type: 'error',
          text: 'The sync could not be completed. Please try again, or contact support if it keeps happening.',
        });
      }
    } catch (err) {
      // The backend returns a friendly 409 detail ("A Vacancy Report sync is
      // already running") when one is in progress; friendlyApiError passes that
      // through, so it surfaces here as-is.
      if (isMountedRef.current) {
        setSyncMessage({
          type: 'error',
          text: err instanceof Error ? err.message : 'Sync failed. Please try again.',
        });
      }
    } finally {
      if (isMountedRef.current) {
        setIsSyncing(false);
      }
    }
  };

  const handleExport = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const exportRows = filteredRows.map((row) =>
        visibleColumns.reduce<Record<string, unknown>>((acc, col) => {
          acc[col.label] = row[col.key];
          return acc;
        }, {}),
      );
      await exportQueryResponse(exportRows, `${title} Data`, 'table');
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setIsExporting(false);
    }
  };

  const buildTableAskMoreContext = (contextQuery: string) => {
    const data = filteredRows.slice(0, 500).map((row) =>
      visibleColumns.reduce<Record<string, unknown>>((record, column) => {
        record[column.label] = row[column.key];
        return record;
      }, {})
    );
    return {
      title: `${title} Data`,
      users_query: contextQuery,
      generated_sql_query: 'Built-in Vacancy Report table',
      query_response: JSON.stringify(data),
      data,
      last_data_snapshot: data,
      source_row_count: filteredRows.length,
      visualization_type: 'table',
    };
  };

  return (
    <section className="rounded-xl border border-slate-200 bg-white/95 shadow-sm dark:border-slate-700 dark:bg-slate-900/90">
      <div className="flex flex-col gap-4 border-b border-slate-200 px-6 py-5 dark:border-slate-700 md:flex-row md:items-center md:justify-between">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-wider text-blue-600 dark:text-blue-400">
            Vacancy Report
          </div>
          <h2 className="mt-1 text-xl font-bold text-slate-950 dark:text-slate-50">{title}</h2>
        </div>
        {syncMessage && (
          <div
            role="status"
            className={`rounded-md px-3 py-2 text-sm font-medium ${
              syncMessage.type === 'success'
                ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300'
                : 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300'
            }`}
          >
            {syncMessage.text}
          </div>
        )}
      </div>

      <div className="px-6 py-5">
        {error ? (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
            {error}
          </div>
        ) : (
          <>
            {kpi && (
              <div className="mb-6 flex flex-wrap items-start gap-10">
                <div>
                  {isLoading ? (
                    <div className="h-10 w-24 animate-pulse rounded bg-slate-200 dark:bg-slate-700" />
                  ) : (
                    <div className="text-4xl font-extrabold text-slate-950 dark:text-slate-50">
                      {formatRecordCount(summary?.open_count ?? 0)}
                    </div>
                  )}
                  <div className="mt-1 text-sm font-semibold text-slate-500 dark:text-slate-400">{kpi.count}</div>
                </div>
                <div>
                  {isLoading ? (
                    <div className="h-10 w-24 animate-pulse rounded bg-slate-200 dark:bg-slate-700" />
                  ) : (
                    <div className="text-4xl font-extrabold text-slate-950 dark:text-slate-50">
                      {summary?.average_ageing_days ?? 0}
                    </div>
                  )}
                  <div className="mt-1 text-sm font-semibold text-slate-500 dark:text-slate-400">
                    {kpi.ageing}
                  </div>
                </div>
              </div>
            )}

            <div className="overflow-hidden rounded-lg border border-slate-200 dark:border-slate-700">
              <div className="flex flex-col gap-3 border-b border-slate-200 px-4 py-4 dark:border-slate-700 md:flex-row md:items-center md:justify-between">
                <div>
                  <h3 className="font-bold text-slate-800 dark:text-slate-100">{tableTitles[activeTab]}</h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Showing {filteredRows.length ? startIndex + 1 : 0} to {endIndex} of {filteredRows.length} rows
                    {filteredRows.length !== rows.length ? ` (filtered from ${rows.length} rows)` : ''}
                  </p>
                </div>
                <div className="flex w-full flex-col gap-2 md:w-auto md:flex-row md:items-center">
                  <label className="flex h-10 items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                    Show:
                    <select
                      value={pageSize}
                      onChange={(event) => setPageSize(Number(event.target.value))}
                      className="h-10 rounded-md border border-slate-300 bg-white px-3 text-sm font-medium dark:border-slate-600 dark:bg-slate-800"
                    >
                      {[10, 25, 50, 100].map((size) => (
                        <option key={size} value={size}>
                          {size}
                        </option>
                      ))}
                    </select>
                  </label>
                  <div ref={columnMenuRef} className="relative">
                    <button
                      type="button"
                      onClick={() => setShowColumnMenu((current) => !current)}
                      className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-700 shadow-sm transition-colors hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
                      title="Show or hide columns"
                    >
                      <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>
                        arrow_drop_down
                      </span>
                      Columns
                    </button>
                    {showColumnMenu && (
                      <div className="absolute right-0 z-30 mt-2 w-80 border border-slate-200 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-900">
                        <div className="px-3 pt-2 text-sm font-semibold text-neutral-800 dark:text-slate-100">
                          Columns
                        </div>
                        <button
                          type="button"
                          onClick={() => setShowColumnMenu((current) => !current)}
                          className="mx-3 mt-1 flex h-9 w-[calc(100%-1.5rem)] items-center justify-between border border-slate-200 bg-white px-2 text-left text-sm text-neutral-500 shadow-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
                        >
                          <span>All</span>
                          <span className="material-symbols-outlined text-neutral-500" style={{ fontSize: '18px' }}>
                            expand_less
                          </span>
                        </button>
                        <div className="max-h-72 overflow-y-auto px-3 py-1">
                          <label className="flex h-8 cursor-pointer items-center gap-3 text-sm text-neutral-800 hover:bg-slate-50 dark:text-slate-100 dark:hover:bg-slate-800">
                            <input
                              type="checkbox"
                              checked={allColumnsVisible}
                              onChange={() => {
                                if (allColumnsVisible) {
                                  handleHideAllColumns();
                                } else {
                                  handleSelectAllColumns();
                                }
                              }}
                              className="h-4 w-4 rounded-none border-neutral-800 bg-neutral-900 text-neutral-900 accent-neutral-900 focus:ring-0"
                            />
                            <span>Select all</span>
                          </label>
                          {selectableColumns.map((column) => {
                            const checked = !hiddenColumnKeys.includes(column.key);
                            return (
                              <label
                                key={`column-toggle-${column.key}`}
                                className="flex h-8 cursor-pointer items-center gap-3 text-sm text-neutral-800 hover:bg-slate-50 dark:text-slate-100 dark:hover:bg-slate-800"
                              >
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => handleToggleColumn(column.key)}
                                  disabled={checked && visibleColumns.length <= 1}
                                  className="h-4 w-4 rounded-none border-neutral-800 bg-neutral-900 text-neutral-900 accent-neutral-900 focus:ring-0 disabled:opacity-50"
                                />
                                <span className="truncate">{column.label}</span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={handleSync}
                    disabled={isSyncing}
                    className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-700 shadow-sm transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
                    title="Fetch the latest data from the source systems now"
                  >
                    <span
                      className={`material-symbols-outlined ${isSyncing ? 'animate-spin' : ''}`}
                      style={{ fontSize: '18px' }}
                    >
                      {isSyncing ? 'progress_activity' : 'sync'}
                    </span>
                    {isSyncing ? 'Syncing…' : 'Sync'}
                  </button>
                  {onAskMore && (
                    <button
                      type="button"
                      onClick={() => {
                        const contextQuery = `Ask about ${title} data`;
                        onAskMore(contextQuery, buildTableAskMoreContext(contextQuery));
                      }}
                      className="inline-flex h-10 items-center justify-center gap-2 rounded-md bg-emerald-500 px-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-emerald-600"
                      title="Ask more about this data"
                    >
                      <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>
                        auto_awesome
                      </span>
                      Ask More
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={handleExport}
                    disabled={isExporting || isLoading || filteredRows.length === 0}
                    className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-blue-600 text-white shadow-sm transition-colors hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Export"
                  >
                    {isExporting ? (
                      <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                    ) : (
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600 dark:bg-slate-800/80 dark:text-slate-300">
                    <tr>
                      {visibleColumns.map((column) => (
                        <th key={column.key} className="whitespace-nowrap px-4 py-3 text-left font-bold">
                          <button
                            type="button"
                            onClick={() => handleTableSort(column.key)}
                            className="group inline-flex items-center gap-1.5 text-left font-bold text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white"
                            title={`Sort by ${column.label}`}
                          >
                            <span>{column.label}</span>
                            <span
                              className={`material-symbols-outlined text-[15px] leading-none transition-opacity ${
                                tableSort?.key === column.key ? 'opacity-100' : 'opacity-35 group-hover:opacity-80'
                              }`}
                              aria-hidden="true"
                              style={{ fontSize: '15px' }}
                            >
                              {tableSort?.key === column.key
                                ? tableSort.direction === 'asc'
                                  ? 'arrow_upward'
                                  : 'arrow_downward'
                                : 'unfold_more'}
                            </span>
                          </button>
                        </th>
                      ))}
                    </tr>
                    <tr>
                      {visibleColumns.map((column) => (
                        <th key={`${column.key}-filter`} className="px-4 pb-3">
                          <input
                            type="search"
                            value={columnFilters[column.key] || ''}
                            onChange={(event) => handleColumnFilterChange(column.key, event.target.value)}
                            className="w-full min-w-40 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs font-normal text-slate-700 placeholder:text-slate-400 focus:border-blue-400 focus:outline-none dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                            placeholder={`Filter ${column.label}...`}
                          />
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                    {isLoading ? (
                      <TableLoadingRows columnCount={visibleColumns.length} rowCount={Math.min(pageSize, 8)} />
                    ) : displayedRows.length === 0 ? (
                      <tr>
                        <td className="px-4 py-8 text-center text-slate-500" colSpan={Math.max(visibleColumns.length, 1)}>
                          No data available
                        </td>
                      </tr>
                    ) : (
                      displayedRows.map((row, rowIndex) => (
                        <tr
                          key={`${activeTab}-${startIndex + rowIndex}`}
                          className="bg-white text-slate-700 hover:bg-slate-50 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800/70"
                        >
                          {visibleColumns.map((column) => {
                            const cellValue = renderCellValue(column, row[column.key]);
                            const numericValue = Number(row[column.key]);
                            const isVariance = isVarianceColumn(column.key) && Number.isFinite(numericValue);
                            const varianceClass = isVariance
                              ? numericValue < 0
                                ? 'text-red-600 dark:text-red-400 font-semibold'
                                : numericValue > 0
                                ? 'text-blue-600 dark:text-blue-400 font-semibold'
                                : ''
                              : '';
                            return (
                              <td key={column.key} className={`whitespace-nowrap px-4 py-3 ${varianceClass}`}>
                                {cellValue}
                              </td>
                            );
                          })}
                        </tr>
                      ))
                    )}
                  </tbody>
                  {showTotalsRow && !isLoading && filteredRows.length > 0 && (
                    <tfoot>
                      <tr className="border-t-2 border-slate-300 bg-slate-100 font-bold text-slate-800 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100">
                        {visibleColumns.map((column, columnIndex) => {
                          if (columnIndex === 0) {
                            return (
                              <td key={column.key} className="whitespace-nowrap px-4 py-3">
                                Total
                              </td>
                            );
                          }
                          if (!(column.key in columnTotals)) {
                            return <td key={column.key} className="px-4 py-3" />;
                          }
                          const total = columnTotals[column.key];
                          const isVariance = isVarianceColumn(column.key);
                          const varianceClass = isVariance
                            ? total < 0
                              ? 'text-red-600 dark:text-red-400'
                              : total > 0
                              ? 'text-blue-600 dark:text-blue-400'
                              : ''
                            : '';
                          return (
                            <td key={column.key} className={`whitespace-nowrap px-4 py-3 ${varianceClass}`}>
                              {formatNumber(total)}
                            </td>
                          );
                        })}
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>

              {filteredRows.length > pageSize && (
                <div className="flex flex-col gap-3 border-t border-slate-200 px-4 py-3 dark:border-slate-700 sm:flex-row sm:items-center sm:justify-between">
                  <div className="text-sm text-slate-500 dark:text-slate-400">
                    Page {safeCurrentPage} of {totalPages}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                      disabled={safeCurrentPage === 1}
                      className="rounded-md border border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800"
                    >
                      Previous
                    </button>
                    {pageNumbers.map((page) => (
                      <button
                        key={page}
                        type="button"
                        onClick={() => setCurrentPage(page)}
                        className={`min-w-9 rounded-md border px-3 py-1.5 text-sm font-semibold transition-colors ${
                          safeCurrentPage === page
                            ? 'border-blue-600 bg-blue-600 text-white'
                            : 'border-slate-300 text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                      disabled={safeCurrentPage === totalPages}
                      className="rounded-md border border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>

            {chartCountTitles[activeTab] && (
              <div className="mt-6">
                <VacancyReportChartGrid
                  charts={charts}
                  isLoading={isLoading}
                  countByStoreTitle={chartCountTitles[activeTab]!.byStore}
                  countByProvinceTitle={chartCountTitles[activeTab]!.byProvince}
                />
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default VacancyReportDashboard;
